// Web Serial API implementation (Chrome/Edge only).
// Falls back to a "not supported" response on unsupported browsers.
//
// Unlike Electron's serialport (path-addressable, no permission model), Web Serial
// ports are opaque objects gated behind a per-origin permission grant:
//   - navigator.serial.getPorts() returns already-granted ports, no gesture needed.
//   - navigator.serial.requestPort() shows the browser's device picker and MUST be
//     called from inside a real user click — calling it from a background poll throws.
// useBarcodeScanner (shared/barcodeScanner.ts) was written against Electron's
// path-based, no-permission model, so this adapter bridges the gap: it assigns each
// granted port a stable synthetic "path" and reshapes getInfo()'s numeric
// usbVendorId/usbProductId into the hex-string vendorId/productId shape
// isBarcodeScannerPort() expects. openSerialPort() only ever opens an
// already-granted port by that path — it never calls requestPort() itself, so it's
// safe to call from useBarcodeScanner's automatic reconnect poll. The one-time
// pairing gesture is a separate method, requestScannerPort(), for a real click handler.

type SerialDataCallback = (event: unknown, data: string) => void;
const dataListeners: SerialDataCallback[] = [];

type WebSerialPortInfo = { usbVendorId?: number; usbProductId?: number };
type WebSerialPort = {
  getInfo(): WebSerialPortInfo;
  open(options: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
};
type NavigatorSerial = {
  getPorts(): Promise<WebSerialPort[]>;
  requestPort(): Promise<WebSerialPort>;
};

function getSerial(): NavigatorSerial | undefined {
  return (navigator as unknown as { serial?: NavigatorSerial }).serial;
}

// Web Serial has no path/COM-name concept — ports are opaque objects that stay the
// same JS instance across getPorts() calls within one page session. Assign each a
// stable synthetic path the first time we see it so path-based open/reconnect keeps
// working the same way it does against Electron's real COM paths.
const portsByPath = new Map<string, WebSerialPort>();
const pathsByPort = new WeakMap<WebSerialPort, string>();
let nextPortSeq = 1;

function pathFor(port: WebSerialPort): string {
  let path = pathsByPort.get(port);
  if (!path) {
    path = `web-serial://${nextPortSeq++}`;
    pathsByPort.set(port, path);
  }
  portsByPath.set(path, port);
  return path;
}

function toHex(n: number | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toString(16).toUpperCase() : '';
}

function describePort(port: WebSerialPort): { path: string; vendorId: string; productId: string } {
  const info = port.getInfo?.() ?? {};
  return {
    path: pathFor(port),
    vendorId: toHex(info.usbVendorId),
    productId: toHex(info.usbProductId),
  };
}

let openPort: WebSerialPort | null = null;
let openReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
let pumpAlive = false;

async function stopPump(): Promise<void> {
  pumpAlive = false;
  if (openReader) {
    try {
      await openReader.cancel();
    } catch {
      /* ignore */
    }
    try {
      openReader.releaseLock();
    } catch {
      /* ignore */
    }
    openReader = null;
  }
}

function startPump(port: WebSerialPort): void {
  if (!port.readable) return;
  pumpAlive = true;
  const reader = port.readable.getReader();
  openReader = reader;
  const decoder = new TextDecoder();
  (async () => {
    try {
      while (pumpAlive) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          const text = decoder.decode(value);
          for (const cb of dataListeners) cb(null, text);
        }
      }
    } catch {
      // Port closed/disconnected mid-read — normal on unplug or explicit close.
    }
  })();
}

async function openPortObject(
  port: WebSerialPort,
  baudRate: number,
): Promise<{ success: boolean; message: unknown }> {
  try {
    if (openPort && openPort !== port) {
      await stopPump();
      try {
        await openPort.close();
      } catch {
        /* ignore */
      }
    }
    await port.open({ baudRate });
    openPort = port;
    startPump(port);
    return { success: true, message: 'Serial port opened' };
  } catch (err: unknown) {
    return { success: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export function createWebSerialApi() {
  return {
    async openSerialPort(
      path?: string,
      baudRate = 9600,
    ): Promise<{ success: boolean; message: unknown }> {
      const serial = getSerial();
      if (!serial) {
        return { success: false, message: 'Web Serial API not supported in this browser' };
      }
      const port = path ? portsByPath.get(path) : undefined;
      if (!port) {
        return {
          success: false,
          message: 'Scanner not paired yet — click "Connect scanner" to grant access',
        };
      }
      return openPortObject(port, baudRate);
    },

    async closeSerialPort(): Promise<{ success: boolean; message: string }> {
      await stopPump();
      if (openPort) {
        try {
          await openPort.close();
        } catch {
          /* ignore */
        }
        openPort = null;
      }
      return { success: true, message: 'Closed' };
    },

    async listSerialPorts(): Promise<{ success: boolean; message: unknown }> {
      const serial = getSerial();
      if (!serial) return { success: false, message: 'Web Serial API not supported' };
      const ports = await serial.getPorts();
      return { success: true, message: ports.map(describePort) };
    },

    onSerialData(callback: SerialDataCallback): () => void {
      dataListeners.push(callback);
      return () => {
        const i = dataListeners.indexOf(callback);
        if (i !== -1) dataListeners.splice(i, 1);
      };
    },

    // Web-only pairing step. Must be called from a real click handler — the browser
    // silently rejects requestPort() calls made outside a user gesture.
    async requestScannerPort(): Promise<{ success: boolean; message: unknown }> {
      const serial = getSerial();
      if (!serial) {
        return { success: false, message: 'Web Serial API not supported in this browser' };
      }
      try {
        const port = await serial.requestPort();
        return { success: true, message: describePort(port) };
      } catch (err: unknown) {
        return { success: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
  };
}
